<template>
    <!--
        Deals table — dynamic columns (driven by current_columns, like the
        legacy table) with per-slug cell renderers in the refreshed style.
        Row click opens the detail drawer (emits "open"); link cells, the
        checkbox and the delete action stop propagation so they don't also
        trigger the drawer.
    -->
    <div :style="wrapStyle">
        <table :style="tableStyle">
            <colgroup>
                <col :style="{ width: '38px' }" />
                <col v-for="c in visibleColumns" :key="'col-' + c.id" :style="{ width: columnWidth(c) }" />
                <col :style="{ width: '40px' }" />
            </colgroup>
            <thead>
                <tr>
                    <th :style="headCellStyle({ sticky: 'left' })">
                        <input type="checkbox" :style="{ margin: '0 auto', display: 'block' }" @click.stop />
                    </th>
                    <th
                        v-for="c in visibleColumns"
                        :key="'th-' + c.id"
                        :style="headCellStyle({ align: columnAlign(c) })"
                        @click="$emit('sort', c.slug)"
                    >
                        <span :style="headLabelStyle(c)">
                            {{ c.name || c.label || c.slug }}
                            <Icon
                                v-if="lastSort === c.slug"
                                :name="descending ? 'arrowDown' : 'arrowUp'"
                                :size="10"
                                :style="{ color: 'var(--tag-red)' }"
                            />
                        </span>
                    </th>
                    <th :style="headCellStyle({ align: 'right' })" />
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="(item, idx) in items"
                    :key="'row-' + (item.id || idx)"
                    :style="rowStyle(idx, item)"
                    @click="$emit('open', item)"
                >
                    <td :style="bodyCellStyle({ sticky: 'left' })">
                        <input type="checkbox" :style="{ margin: '0 auto', display: 'block' }" @click.stop />
                    </td>

                    <td
                        v-for="c in visibleColumns"
                        :key="'td-' + (item.id || idx) + '-' + c.id"
                        :style="bodyCellStyle({ align: columnAlign(c) })"
                    >
                        <!-- Agency: avatar + name link (opens record in new tab) -->
                        <template v-if="c.slug === 'agency'">
                            <span :style="agencyCellStyle">
                                <Avatar :name="linkName(item[c.slug])" :size="18" />
                                <a
                                    v-if="linkHref(item[c.slug])"
                                    :href="linkHref(item[c.slug])"
                                    target="_blank"
                                    :style="linkStyle"
                                    @click.stop
                                >{{ linkName(item[c.slug]) }}</a>
                                <span v-else :style="linkStyle">{{ linkName(item[c.slug]) }}</span>
                            </span>
                        </template>

                        <!-- Status pill -->
                        <template v-else-if="c.slug === 'status'">
                            <DealStatusPill :status="String(item[c.slug] || '')" />
                        </template>

                        <!-- ID -->
                        <template v-else-if="c.slug === 'id'">
                            <span class="tag-mono" :style="idStyle">#{{ item[c.slug] }}</span>
                        </template>

                        <!-- Date -->
                        <template v-else-if="c.slug === 'date'">
                            <span class="tag-mono tag-tnum" :style="{ color: 'var(--text-muted)' }">{{ item[c.slug] || '—' }}</span>
                        </template>

                        <!-- Partner: dot + name -->
                        <template v-else-if="c.slug === 'partner'">
                            <span :style="partnerCellStyle">
                                <span :style="partnerDotStyle" />
                                {{ linkName(item[c.slug]) || '—' }}
                            </span>
                        </template>

                        <!-- Money columns -->
                        <template v-else-if="moneySlugs.includes(c.slug)">
                            <Money :value="item[c.slug]" />
                        </template>

                        <!-- Link cells (name/brand) -->
                        <template v-else-if="c.is_link">
                            <a
                                v-if="linkHref(item[c.slug])"
                                :href="linkHref(item[c.slug])"
                                :style="linkStyle"
                                @click.stop
                            >{{ linkName(item[c.slug]) }}</a>
                            <span v-else :style="linkStyle">{{ linkName(item[c.slug]) }}</span>
                        </template>

                        <!-- Fallback text -->
                        <template v-else>
                            <span :style="plainCellStyle">{{ item[c.slug] != null && item[c.slug] !== '' ? item[c.slug] : '—' }}</span>
                        </template>
                    </td>

                    <td :style="bodyCellStyle({ align: 'right' })">
                        <button
                            v-if="canDelete"
                            type="button"
                            class="btn btn--icon btn--xs btn--ghost"
                            :style="{ color: 'var(--text-faint)' }"
                            @click.stop="$emit('delete', item.id)"
                        >
                            <Icon name="trash" />
                        </button>
                    </td>
                </tr>

                <tr v-if="!items.length">
                    <td :colspan="visibleColumns.length + 2" :style="emptyCellStyle">No deals match your filters.</td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script>
import Icon from "../../Agencies/_atoms/Icon.vue";
import Avatar from "../../Agencies/_atoms/Avatar.vue";
import Money from "../../Agencies/_atoms/Money.vue";
import DealStatusPill from "./DealStatusPill.vue";

const MONEY_SLUGS = ["amount_invoiced", "amount_collect", "commission"];
const RIGHT_ALIGN = [...MONEY_SLUGS];
const COL_WIDTHS = {
    id: "56px",
    date: "92px",
    agency: "200px",
    status: "130px",
    name: "260px",
    partner: "160px",
    amount_invoiced: "120px",
    amount_collect: "120px",
    commission: "120px",
};

export default {
    name: "DealsTable",
    components: { Icon, Avatar, Money, DealStatusPill },
    props: {
        items: { type: Array, default: () => [] },
        columns: { type: Array, default: () => [] },
        lastSort: { type: String, default: "" },
        descending: { type: Boolean, default: false },
        openId: { default: null },
        canDelete: { type: Boolean, default: false },
    },
    computed: {
        moneySlugs() { return MONEY_SLUGS; },
        visibleColumns() {
            return (this.columns || [])
                .filter((c) => c.status === "show")
                .sort((a, b) => (a.position || 0) - (b.position || 0));
        },
        wrapStyle() {
            return { position: "relative", overflow: "auto", flex: "1 1 auto" };
        },
        tableStyle() {
            return { width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: "var(--table-fz)", lineHeight: 1.35 };
        },
        agencyCellStyle() {
            return { display: "inline-flex", alignItems: "center", gap: "8px", minWidth: 0 };
        },
        linkStyle() {
            return { fontWeight: 500, color: "var(--text)", textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
        },
        idStyle() {
            return { color: "var(--text-faint)", fontSize: "calc(var(--table-fz) - 1px)" };
        },
        partnerCellStyle() {
            return { display: "inline-flex", alignItems: "center", gap: "6px", color: "var(--text-muted)" };
        },
        partnerDotStyle() {
            return { width: "6px", height: "6px", borderRadius: "50%", background: "color-mix(in oklch, var(--text-muted) 50%, transparent)" };
        },
        plainCellStyle() {
            return { color: "var(--text-muted)" };
        },
        emptyCellStyle() {
            return { padding: "32px 16px", textAlign: "center", color: "var(--text-faint)", fontSize: "13px" };
        },
    },
    methods: {
        columnWidth(c) {
            return COL_WIDTHS[c.slug] || "120px";
        },
        columnAlign(c) {
            return RIGHT_ALIGN.includes(c.slug) ? "right" : "left";
        },
        headCellStyle({ align = "left", sticky = null } = {}) {
            return {
                padding: "8px var(--row-px, 12px)",
                textAlign: align,
                fontSize: "var(--header-fz)",
                fontWeight: 500,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "var(--text-muted)",
                background: "var(--surface)",
                borderBottom: "1px solid var(--border)",
                position: "sticky",
                top: 0,
                left: sticky === "left" ? 0 : undefined,
                zIndex: sticky === "left" ? 3 : 2,
                whiteSpace: "nowrap",
                cursor: "pointer",
                userSelect: "none",
            };
        },
        bodyCellStyle({ align = "left", sticky = null } = {}) {
            return {
                padding: "var(--cell-py, 8px) var(--row-px, 12px)",
                textAlign: align,
                borderBottom: "1px solid var(--border-faint)",
                whiteSpace: "nowrap",
                verticalAlign: "middle",
                height: "var(--row-h, 36px)",
                color: "var(--text)",
                position: sticky === "left" ? "sticky" : undefined,
                left: sticky === "left" ? 0 : undefined,
                background: sticky === "left" ? "inherit" : undefined,
                zIndex: sticky === "left" ? 1 : undefined,
            };
        },
        headLabelStyle(c) {
            return { display: "inline-flex", alignItems: "center", gap: "5px", color: this.lastSort === c.slug ? "var(--text)" : undefined };
        },
        rowStyle(idx, item) {
            const open = this.openId != null && String(this.openId) === String(item.id);
            return {
                background: open
                    ? "var(--tag-red-soft)"
                    : idx % 2 === 1
                      ? "var(--surface-2)"
                      : "var(--surface)",
                cursor: "pointer",
                boxShadow: open ? "inset 2px 0 0 var(--tag-red)" : "none",
            };
        },
        // Link cells may be a {name, link} object or a plain string.
        linkName(v) {
            if (v && typeof v === "object") return v.name || "";
            return v || "";
        },
        linkHref(v) {
            if (v && typeof v === "object") return v.link || null;
            return null;
        },
    },
};
</script>
